import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  elementColours,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { ArrowButton, Container, Heading, HtmlContent } from './ui';

const StyledAccordion = styled.section`
  ${sectionMargins()};
`;

const StyledInner = styled.div`
  display: grid;
`;

const StyledHeader = styled.header`
  margin-bottom: 20px;

  ${({ isDetail }) => {
    if (!isDetail) {
      return css`
        margin-right: auto;
        margin-left: auto;
        max-width: 740px;
        text-align: center;
      `;
    }
  }}

  ${minBreakpointQuery.small`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 30px;
  `}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 10px;

  ${minBreakpointQuery.small`
    margin-top: 15px;
  `}
`;

const StyledItems = styled.div`
  border-top: solid 1px ${standardColours.grey};
`;

const StyledItem = styled.article`
  padding: 15px;
  border-bottom: solid 1px ${standardColours.grey};

  ${minBreakpointQuery.small`
    padding: 20px;
  `}

  &:nth-child(odd) {
    background-color: ${standardColours.lighterGrey};
  }
`;

const StyledSubHeading = styled.h3`
  position: relative;
  padding-right: 30px;
  ${fontSize(18)};
  line-height: 1.5;
  cursor: pointer;
  font-weight: ${fontWeights.regular};

  ${minBreakpointQuery.small`
    padding-right: 35px;
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.large`
    padding-right: 40px;
  `}

  span {
    border-bottom: 1px solid ${standardColours.transparent};
    transition: ${standardTransition('border-bottom-color')};

    &:hover {
      border-bottom-color: ${elementColours.textColour};
    }
  }

  &:after {
    content: ${({ display }) => (display ? "'-'" : "'+'")};
    position: absolute;
    top: 50%;
    right: 0;
    color: ${standardColours.darkGrey};
    ${fontSize(30)};
    transform: translateY(-50%);

    ${minBreakpointQuery.small`
      ${fontSize(35)};
    `}

    ${minBreakpointQuery.large`
      ${fontSize(40)};
    `}
  }
`;

const StyledContent = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}

  ${({ display }) => {
    if (!display) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const StyledButton = styled(ArrowButton)`
  justify-self: center;
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const Accordion = ({ heading, text, items, link, isDetail }) => {
  const [activeItem, setActiveItem] = useState();

  return (
    <StyledAccordion>
      <Container>
        <StyledInner>
          <StyledHeader isDetail={isDetail}>
            <Heading alt={true}>{heading}</Heading>
            {text && (
              <StyledText>
                <p>{text}</p>
              </StyledText>
            )}
          </StyledHeader>
          <StyledItems>
            {items.map(({ heading, content }, i) => {
              const display = activeItem === i;
              return (
                <StyledItem key={i}>
                  <StyledSubHeading
                    display={display}
                    onClick={() => setActiveItem(display ? undefined : i)}
                  >
                    <span>{heading}</span>
                  </StyledSubHeading>
                  <StyledContent html={content} display={display} />
                </StyledItem>
              );
            })}
          </StyledItems>
          {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
        </StyledInner>
      </Container>
    </StyledAccordion>
  );
};

export default Accordion;
